import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Spin, Empty, message, Modal, Input, Button, Popconfirm, Tooltip } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import {
  DndContext,
  DragOverlay,
  MeasuringStrategy,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import { SortableContext, arrayMove, verticalListSortingStrategy } from "@dnd-kit/sortable";

import {
  getBoardListWithRootCategory,
  newGetCategory,
  reorderCategory,
  changeParentCategory,
  newPostCategory,
  putCategory,
  deleteCategory,
} from "../../../api/categoryAPI";
import {
  getPostList,
  changeCategory,
  reorderPosts,
  getPostData,
  putPost,
  isPostDetailBlocked,
  createPost,
  postPost,
} from "../../../api/postAPI";
import { getPermittedUsersForCategory, addCategoryPermission } from "../../../api/adminAPI";

import { dndId, parseDndId, type BoardNode, type CatNode, type PostNode } from "./types";
import { SortableRow, DroppableRow, OverlayRow } from "./rows";
import { getBoardHeaderInfo } from "../config/boardHeaderStructure";

const APPEND_ORDER = 1;

const sortByListOrder = <T extends { listOrder?: number }>(list: T[]): T[] =>
  [...list].sort((a, b) => (a.listOrder ?? 0) - (b.listOrder ?? 0));

const CategoryDndEditor = () => {
  const navigate = useNavigate();
  const [boards, setBoards] = useState<BoardNode[]>([]);
  const [mainCats, setMainCats] = useState<CatNode[]>([]);
  const [subCats, setSubCats] = useState<CatNode[]>([]);
  const [posts, setPosts] = useState<PostNode[]>([]);

  const [selectedBoardId, setSelectedBoardId] = useState<number | null>(null);
  const [selectedMainId, setSelectedMainId] = useState<number | null>(null);
  const [selectedSubId, setSelectedSubId] = useState<number | null>(null);

  const [loadingMains, setLoadingMains] = useState(false);
  const [loadingSubs, setLoadingSubs] = useState(false);
  const [loadingPosts, setLoadingPosts] = useState(false);

  const [activeLabel, setActiveLabel] = useState<string | null>(null);

  // 게시글을 다른 게시판으로 드래그 중일 때, 메인 카테고리 컬럼에 "대상 게시판"의 메인들을 미리보기로 노출
  const [dragPreview, setDragPreview] = useState<{ boardId: number; mains: CatNode[] } | null>(null);
  // 게시판별 메인 카테고리 캐시 (드래그 중 미리보기를 동기적으로 보여주기 위해 미리 로드)
  const mainsCacheRef = useRef<Record<number, CatNode[]>>({});

  // 게시글 드래그 중 특정 "메인" 위에 올리면, 서브 카테고리 컬럼에 그 메인의 서브들을 미리보기로 노출
  const [subPreview, setSubPreview] = useState<{ mainId: number; mainName: string; subs: CatNode[] } | null>(null);
  const subsCacheRef = useRef<Record<number, CatNode[]>>({}); // mainId -> subs
  const subsFetchingRef = useRef<Set<number>>(new Set());
  const hoveredMainRef = useRef<number | null>(null); // 비동기 로드 후 아직 같은 메인을 호버 중인지 판별
  const isPostDraggingRef = useRef(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const selectedBoard = useMemo(
    () => boards.find((b) => b.boardId === selectedBoardId) || null,
    [boards, selectedBoardId]
  );

  // 게시글이 속한 leaf 카테고리:
  // - 서브 선택 시 → 서브 카테고리의 게시글
  // - 서브 미선택 & 메인 선택 시 → 메인 카테고리에 "직접" 속한 게시글 (서브 유무와 무관)
  const leafCategoryId =
    selectedSubId != null
      ? selectedSubId
      : selectedMainId != null
      ? selectedMainId
      : null;
  const leafCategoryIdRef = useRef(leafCategoryId);
  leafCategoryIdRef.current = leafCategoryId;

  // 게시글 컬럼이 현재 어떤 카테고리의 글을 보여주는지 표시용
  const leafLabel =
    selectedSubId != null
      ? subCats.find((c) => c.id === selectedSubId)?.name
      : selectedMainId != null
      ? `${mainCats.find((c) => c.id === selectedMainId)?.name ?? ""} (메인 직속)`
      : undefined;

  /* -------------------------------- 데이터 로딩 -------------------------------- */
  useEffect(() => {
    (async () => {
      const data = await getBoardListWithRootCategory();
      const list: BoardNode[] = (data || []).map((b: any) => ({
        boardId: b.board_id,
        name: b.name,
        categoryType: b.categoryType,
        rootCategoryId: b.rootCategories?.[0]?.id,
      }));
      setBoards(list);
      if (list.length > 0) selectBoard(list[0]);

      // 게시판별 메인 카테고리 미리 로드 (게시글 크로스-보드 이동 미리보기용)
      list.forEach(async (b) => {
        if (!b.rootCategoryId) return;
        try {
          const mains = sortByListOrder(((await newGetCategory(b.rootCategoryId)) || []) as CatNode[]);
          mainsCacheRef.current[b.boardId] = mains;
        } catch {
          /* noop */
        }
      });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadMains = async (rootCategoryId?: number): Promise<CatNode[]> => {
    if (!rootCategoryId) {
      setMainCats([]);
      return [];
    }
    setLoadingMains(true);
    try {
      const data = sortByListOrder(((await newGetCategory(rootCategoryId)) || []) as CatNode[]);
      setMainCats(data);
      return data;
    } catch {
      setMainCats([]);
      return [];
    } finally {
      setLoadingMains(false);
    }
  };

  const loadSubs = async (mainId: number): Promise<CatNode[]> => {
    setLoadingSubs(true);
    try {
      const data = sortByListOrder(((await newGetCategory(mainId)) || []) as CatNode[]);
      setSubCats(data);
      return data;
    } catch {
      setSubCats([]);
      return [];
    } finally {
      setLoadingSubs(false);
    }
  };

  const loadPosts = async (categoryId: number) => {
    setLoadingPosts(true);
    try {
      // criteria 는 백엔드에서 확실히 지원하는 "id" 사용 후, listOrder 필드가 있으면 클라이언트에서 재정렬
      const data = await getPostList(1, 9999, categoryId, "id", "ASC");
      const raw: any[] = Array.isArray(data) ? data : data?.content ?? [];
      const list: PostNode[] = raw.map((p) => ({
        id: p.id,
        title: p.title,
        listOrder: p.listOrder,
      }));
      setPosts(sortByListOrder(list));
    } catch {
      setPosts([]);
    } finally {
      setLoadingPosts(false);
    }
  };

  const selectBoard = async (board: BoardNode) => {
    setSelectedBoardId(board.boardId);
    setSelectedMainId(null);
    setSelectedSubId(null);
    setSubCats([]);
    setPosts([]);
    await loadMains(board.rootCategoryId);
  };

  const selectMain = async (mainId: number) => {
    setSelectedMainId(mainId);
    setSelectedSubId(null);
    setSubCats([]);
    setPosts([]);
    await loadSubs(mainId);
    // 메인에 직접 속한 게시글을 항상 로드 (서브가 있어도 메인 직속 게시글이 있을 수 있음).
    // 서브를 선택하면 그때 서브의 게시글로 교체된다.
    await loadPosts(mainId);
  };

  const selectSub = async (subId: number) => {
    setSelectedSubId(subId);
    await loadPosts(subId);
  };

  /* ------------------------- 카테고리 추가/수정/삭제 ------------------------- */
  // catModal: 추가/수정 모달 상태. column 은 어느 컬럼(메인/서브) 대상인지.
  const [catModal, setCatModal] = useState<
    | { type: "add"; column: "main" | "sub" }
    | { type: "edit"; column: "main" | "sub"; target: CatNode }
    | null
  >(null);
  const [catName, setCatName] = useState("");
  const [catSubmitting, setCatSubmitting] = useState(false);

  const reloadMains = async () => {
    if (!selectedBoard?.rootCategoryId) return;
    const mains = await loadMains(selectedBoard.rootCategoryId);
    mainsCacheRef.current[selectedBoard.boardId] = mains; // 드래그 미리보기 캐시도 최신화
  };

  const reloadSubs = async () => {
    if (selectedMainId != null) {
      const subs = await loadSubs(selectedMainId);
      subsCacheRef.current[selectedMainId] = subs;
    }
  };

  const openAddCategory = (column: "main" | "sub") => {
    setCatName("");
    setCatModal({ type: "add", column });
  };

  const openEditCategory = (column: "main" | "sub", target: CatNode) => {
    setCatName(target.name);
    setCatModal({ type: "edit", column, target });
  };

  const submitCatModal = async () => {
    if (!catModal) return;
    const name = catName.trim();
    if (!name) {
      message.warning("카테고리 이름을 입력하세요.");
      return;
    }
    setCatSubmitting(true);
    try {
      if (catModal.type === "add") {
        const type = selectedBoard?.categoryType || "";
        if (catModal.column === "main") {
          if (!selectedBoard?.rootCategoryId) return;
          await newPostCategory(name, selectedBoard.rootCategoryId, type);
          await reloadMains();
          message.success("메인 카테고리가 추가되었습니다.");
        } else {
          if (selectedMainId == null) return;
          const result: any = await newPostCategory(name, selectedMainId, type);
          // 상위(메인) 권한을 새 서브 카테고리에 상속 (기존 편집 탭과 동일 동작)
          const newCategoryId = result?.data;
          if (newCategoryId) {
            try {
              const permittedUserIds: number[] = await getPermittedUsersForCategory(selectedMainId);
              if (permittedUserIds?.length) {
                await Promise.all(
                  permittedUserIds.map((uid) => addCategoryPermission(uid, newCategoryId))
                );
              }
            } catch {
              /* 권한 상속 실패는 무시 (카테고리 생성은 성공) */
            }
          }
          await reloadSubs();
          message.success("서브 카테고리가 추가되었습니다.");
        }
      } else {
        await putCategory(catModal.target.id, name);
        if (catModal.column === "main") await reloadMains();
        else await reloadSubs();
        message.success("카테고리가 수정되었습니다.");
      }
      setCatModal(null);
    } catch {
      message.error("처리에 실패했습니다.");
    } finally {
      setCatSubmitting(false);
    }
  };

  const deleteCat = async (column: "main" | "sub", id: number) => {
    try {
      await deleteCategory(id);
      message.success("카테고리가 삭제되었습니다.");
      if (column === "main") {
        if (selectedMainId === id) {
          setSelectedMainId(null);
          setSelectedSubId(null);
          setSubCats([]);
          setPosts([]);
        }
        await reloadMains();
      } else {
        if (selectedSubId === id) {
          setSelectedSubId(null);
          if (selectedMainId != null) await loadPosts(selectedMainId);
        }
        await reloadSubs();
      }
    } catch {
      message.error("카테고리 삭제에 실패했습니다.");
    }
  };

  /* -------------------------- 게시글 추가/이름 수정 -------------------------- */
  // postModal: 추가(현재 leaf 카테고리에 새 게시글) 또는 이름 수정 대상
  const [postModal, setPostModal] = useState<
    { type: "add" } | { type: "edit"; target: PostNode } | null
  >(null);
  const [postTitle, setPostTitle] = useState("");
  const [postSubmitting, setPostSubmitting] = useState(false);

  const openEditPost = (post: PostNode) => {
    setPostTitle(post.title);
    setPostModal({ type: "edit", target: post });
  };

  const openAddPost = () => {
    setPostTitle("");
    setPostModal({ type: "add" });
  };

  // 기존 게시글 추가 흐름과 동일: 임시 게시글 생성(createPost) → 제목 저장(postPost)
  const addPost = async (title: string) => {
    if (leafCategoryId == null) return;
    const categoryId = leafCategoryId;
    const postId = await createPost(categoryId);
    if (!postId) throw new Error("create failed");
    // POST /post 는 성공 시 204(본문 없음)라 반환값으로 성공 여부를 판단할 수 없다
    await postPost(postId, title, null);
    // 저장 중 다른 카테고리로 이동했으면 목록을 덮어쓰지 않는다
    if (categoryId === leafCategoryIdRef.current) await loadPosts(categoryId);
    message.success("게시글이 추가되었습니다.");
  };

  const submitPostModal = async () => {
    if (!postModal) return;
    const title = postTitle.trim();
    if (!title) {
      message.warning("게시글 이름을 입력하세요.");
      return;
    }
    setPostSubmitting(true);
    if (postModal.type === "add") {
      try {
        await addPost(title);
        setPostModal(null);
      } catch {
        message.error("게시글 추가에 실패했습니다.");
      } finally {
        setPostSubmitting(false);
      }
      return;
    }
    const target = postModal.target;
    try {
      // 제목만 바꾸되 본문(content)이 지워지지 않도록 최신 게시글을 조회해 기존 content 를 그대로 전송
      const detail = await getPostData(target.id);
      if (isPostDetailBlocked(detail)) {
        message.error("게시글에 접근할 수 없습니다.");
        return;
      }
      const content = detail?.content ?? "";
      const ok = await putPost(target.id, title, content);
      if (ok === false) throw new Error("update failed");
      setPosts((prev) => prev.map((p) => (p.id === target.id ? { ...p, title } : p)));
      message.success("게시글 이름이 수정되었습니다.");
      setPostModal(null);
    } catch {
      message.error("게시글 이름 수정에 실패했습니다.");
    } finally {
      setPostSubmitting(false);
    }
  };

  // 게시글 행 클릭 → 게시글 페이지로 이동 (다른 관리자 화면과 같은 from=admin 브레드크럼 파라미터)
  const goToPost = (post: PostNode) => {
    const params = new URLSearchParams();
    params.set("from", "admin");
    params.set("title", post.title || "제목 없음");
    const main = mainCats.find((c) => c.id === selectedMainId);
    const sub = subCats.find((c) => c.id === selectedSubId);
    if (main) {
      params.set("main", String(main.id));
      params.set("mainName", main.name);
    }
    if (sub) {
      params.set("sub", String(sub.id));
      params.set("subName", sub.name);
    }
    navigate(`/post/${post.id}?${params.toString()}`);
  };

  /* --------------------------------- DnD 처리 -------------------------------- */
  const handleDragStart = (event: DragStartEvent) => {
    const a = parseDndId(String(event.active.id));
    isPostDraggingRef.current = a.type === "post";
    let label = "";
    if (a.type === "board") label = boards.find((b) => b.boardId === a.id)?.name ?? "";
    else if (a.type === "main") label = mainCats.find((c) => c.id === a.id)?.name ?? "";
    else if (a.type === "sub") label = subCats.find((c) => c.id === a.id)?.name ?? "";
    else if (a.type === "post") label = posts.find((p) => p.id === a.id)?.title ?? "";
    setActiveLabel(label);
  };

  // 메인의 서브 카테고리를 (없으면 로드해서) 미리보기로 세팅
  const fetchSubsForPreview = (mainId: number, mainName: string) => {
    if (subsFetchingRef.current.has(mainId)) return;
    subsFetchingRef.current.add(mainId);
    newGetCategory(mainId)
      .then((data) => {
        const subs = sortByListOrder(((data || []) as CatNode[]));
        subsCacheRef.current[mainId] = subs;
        subsFetchingRef.current.delete(mainId);
        // 로드가 끝났을 때 여전히 같은 메인을 호버 중이면 반영
        if (isPostDraggingRef.current && hoveredMainRef.current === mainId) {
          setSubPreview({ mainId, mainName, subs });
        }
      })
      .catch(() => {
        subsFetchingRef.current.delete(mainId);
      });
  };

  const reorderMain = async (activeId: number, overId: number) => {
    const oldIndex = mainCats.findIndex((c) => c.id === activeId);
    const newIndex = mainCats.findIndex((c) => c.id === overId);
    if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) return;
    const next = arrayMove(mainCats, oldIndex, newIndex);
    setMainCats(next);
    await reorderCategory(next.map((c, i) => ({ categoryId: c.id, listOrder: i + 1 })));
    message.success("메인 카테고리 순서가 변경되었습니다.");
  };

  const reorderSub = async (activeId: number, overId: number) => {
    const oldIndex = subCats.findIndex((c) => c.id === activeId);
    const newIndex = subCats.findIndex((c) => c.id === overId);
    if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) return;
    const next = arrayMove(subCats, oldIndex, newIndex);
    setSubCats(next);
    await reorderCategory(next.map((c, i) => ({ categoryId: c.id, listOrder: i + 1 })));
    message.success("서브 카테고리 순서가 변경되었습니다.");
  };

  const reorderPost = async (activeId: number, overId: number) => {
    const oldIndex = posts.findIndex((p) => p.id === activeId);
    const newIndex = posts.findIndex((p) => p.id === overId);
    if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) return;
    const next = arrayMove(posts, oldIndex, newIndex);
    setPosts(next);
    await reorderPosts(next.map((p, i) => ({ postId: p.id, newOrder: i + 1 })));
    message.success("게시글 순서가 변경되었습니다.");
  };

  const movePost = async (postId: number, targetCategoryId: number) => {
    if (targetCategoryId === leafCategoryId) return; // 같은 카테고리로 이동은 무시
    await changeCategory([{ postId, categoryId: targetCategoryId, listOrder: APPEND_ORDER }]);
    setPosts((prev) => prev.filter((p) => p.id !== postId));
    message.success("게시글을 다른 카테고리로 이동했습니다.");
  };

  const moveSub = async (subId: number, targetMainId: number) => {
    if (targetMainId === selectedMainId) return; // 같은 부모면 무시
    await changeParentCategory(subId, targetMainId, APPEND_ORDER);
    setSubCats((prev) => prev.filter((c) => c.id !== subId));
    message.success("서브 카테고리를 다른 메인 카테고리로 이동했습니다.");
  };

  const moveMain = async (mainId: number, targetBoardId: number) => {
    const targetBoard = boards.find((b) => b.boardId === targetBoardId);
    const targetRootId = targetBoard?.rootCategoryId;
    if (!targetRootId) {
      message.error("대상 게시판의 루트 카테고리를 찾을 수 없습니다.");
      return;
    }
    if (targetRootId === selectedBoard?.rootCategoryId) return; // 같은 게시판이면 무시
    await changeParentCategory(mainId, targetRootId, APPEND_ORDER);
    setMainCats((prev) => prev.filter((c) => c.id !== mainId));
    message.success("메인 카테고리를 다른 게시판으로 이동했습니다.");
  };

  // 게시글을 드래그해서 "다른 게시판" 행 위에 올리면, 메인 카테고리 컬럼을 그 게시판의 메인들로 전환(미리보기).
  // 이후 그 메인 중 하나에 드롭하면 크로스-보드 이동이 된다.
  const handleDragOver = (event: DragOverEvent) => {
    const a = parseDndId(String(event.active.id));
    if (a.type !== "post") return;
    if (!event.over) return;
    const o = parseDndId(String(event.over.id));

    if (o.type === "board") {
      // 게시판 위: 메인 컬럼을 그 게시판 메인들로 미리보기, 서브 미리보기는 초기화
      hoveredMainRef.current = null;
      setSubPreview(null);
      if (o.id === selectedBoardId) {
        setDragPreview(null); // 현재 게시판이면 원래 메인 목록으로
      } else {
        const mains = mainsCacheRef.current[o.id];
        if (mains) setDragPreview((prev) => (prev?.boardId === o.id ? prev : { boardId: o.id, mains }));
      }
      return;
    }

    if (o.type === "main") {
      // 메인 위: 메인 미리보기는 유지하고, 서브 컬럼을 그 메인의 서브들로 미리보기
      hoveredMainRef.current = o.id;
      // 현재 게시판에서 이미 선택된 메인이면 라이브 서브 목록을 그대로 사용
      if (!dragPreview && o.id === selectedMainId) {
        setSubPreview(null);
        return;
      }
      const currentMains = dragPreview ? dragPreview.mains : mainCats;
      const mainName = currentMains.find((c) => c.id === o.id)?.name ?? "";
      const cached = subsCacheRef.current[o.id];
      if (cached) {
        setSubPreview((prev) => (prev?.mainId === o.id ? prev : { mainId: o.id, mainName, subs: cached }));
      } else {
        setSubPreview((prev) => (prev?.mainId === o.id ? prev : { mainId: o.id, mainName, subs: [] }));
        fetchSubsForPreview(o.id, mainName);
      }
      return;
    }

    // o.type === 'sub' | 'post' : 현재 미리보기 유지 (미리보기 서브에 드롭할 수 있도록)
  };

  const resetDragState = () => {
    setActiveLabel(null);
    setDragPreview(null);
    setSubPreview(null);
    hoveredMainRef.current = null;
    isPostDraggingRef.current = false;
  };

  const handleDragCancel = () => {
    resetDragState();
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    resetDragState();
    const { active, over } = event;
    if (!over) return;
    if (active.id === over.id) return;

    const a = parseDndId(String(active.id));
    const o = parseDndId(String(over.id));

    try {
      // 같은 타입 = 같은 컬럼 내 순서 변경
      if (a.type === o.type) {
        if (a.type === "main") await reorderMain(a.id, o.id);
        else if (a.type === "sub") await reorderSub(a.id, o.id);
        else if (a.type === "post") await reorderPost(a.id, o.id);
        return;
      }

      // 게시글을 게시판 행에 바로 드롭 → 불가 안내
      if (a.type === "post" && o.type === "board") {
        message.warning("게시판 직속으로 게시글을 옮길 수 없습니다. 카테고리를 선택해 이동해주세요.");
        return;
      }

      // 다른 타입 = 부모 컬럼의 행 위에 드롭 → 이동(재부모)
      if (a.type === "post" && (o.type === "sub" || o.type === "main")) {
        await movePost(a.id, o.id);
      } else if (a.type === "sub" && o.type === "main") {
        await moveSub(a.id, o.id);
      } else if (a.type === "main" && o.type === "board") {
        await moveMain(a.id, o.id);
      }
    } catch (e) {
      console.error(e);
      message.error("처리 중 오류가 발생했습니다.");
    }
  };

  /* ---------------------------------- 렌더 ---------------------------------- */
  // 게시글 크로스-보드 드래그 중이면 대상 게시판의 메인들을 미리보기로 표시
  const previewBoard = dragPreview ? boards.find((b) => b.boardId === dragPreview.boardId) : null;
  const displayMains = dragPreview ? dragPreview.mains : mainCats;
  // 게시글 드래그 중 특정 메인 위면 그 메인의 서브들을 미리보기로 표시
  const displaySubs = subPreview ? subPreview.subs : subCats;

  const hint = (text: string) => (
    <div style={{ color: "#bfbfbf", fontSize: 13, textAlign: "center", padding: "24px 8px" }}>{text}</div>
  );

  // 카테고리 행 우측의 수정/삭제 액션 (클릭이 행 선택/드래그로 번지지 않게 stopPropagation)
  const catRowActions = (column: "main" | "sub", cat: CatNode) => (
    <span style={{ display: "flex", alignItems: "center", gap: 10 }} onClick={(e) => e.stopPropagation()}>
      <Tooltip title="이름 수정">
        <EditOutlined
          onClick={() => openEditCategory(column, cat)}
          style={{ color: "#8c8c8c", cursor: "pointer" }}
        />
      </Tooltip>
      <Popconfirm
        title="카테고리 삭제"
        description="이 카테고리를 삭제하시겠습니까?"
        onConfirm={() => deleteCat(column, cat.id)}
        okText="삭제"
        cancelText="취소"
      >
        <Tooltip title="삭제">
          <DeleteOutlined style={{ color: "#ff4d4f", cursor: "pointer" }} />
        </Tooltip>
      </Popconfirm>
    </span>
  );

  // 게시글 행 우측의 이름 수정 액션 (클릭이 드래그로 번지지 않게 stopPropagation)
  const postRowActions = (post: PostNode) => (
    <span style={{ display: "flex", alignItems: "center", gap: 10 }} onClick={(e) => e.stopPropagation()}>
      <Tooltip title="이름 수정">
        <EditOutlined
          onClick={() => openEditPost(post)}
          style={{ color: "#8c8c8c", cursor: "pointer" }}
        />
      </Tooltip>
    </span>
  );

  // 게시판 목록을 '게시판 권한 관리' 탭과 동일하게: 헤더 표시명 + 순서 + 회원공간 그룹으로 렌더
  const renderBoards = () => {
    const sorted = [...boards].sort(
      (a, b) => getBoardHeaderInfo(a.name).order - getBoardHeaderInfo(b.name).order
    );
    const out: React.ReactNode[] = [];
    let lastParent: string | undefined;
    sorted.forEach((b) => {
      const info = getBoardHeaderInfo(b.name);
      if (info.parent && info.parent !== lastParent) {
        out.push(
          <div
            key={`grp-${info.parent}`}
            style={{ fontSize: 12, fontWeight: 600, color: "#8c8c8c", padding: "6px 4px 2px" }}
          >
            {info.parent}
          </div>
        );
      }
      lastParent = info.parent;
      out.push(
        <div key={b.boardId} style={{ paddingLeft: info.parent ? 10 : 0 }}>
          <DroppableRow
            dndIdStr={dndId("board", b.boardId)}
            label={info.label}
            selected={b.boardId === selectedBoardId}
            preview={dragPreview?.boardId === b.boardId}
            onClick={() => selectBoard(b)}
          />
        </div>
      );
    });
    return out;
  };

  // 컬럼 헤더의 "추가" 버튼
  const addButton = (column: "main" | "sub", disabled: boolean) => (
    <Button
      size="small"
      type="text"
      icon={<PlusOutlined />}
      disabled={disabled}
      onClick={() => openAddCategory(column)}
    >
      추가
    </Button>
  );

  return (
    <>
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <div style={{ display: "flex", gap: 12, alignItems: "stretch" }}>
        {/* 게시판 */}
        <ColumnShell title="게시판" count={boards.length}>
          {renderBoards()}
        </ColumnShell>

        {/* 메인 카테고리 */}
        <ColumnShell
          title="메인 카테고리"
          subtitle={previewBoard ? `→ ${getBoardHeaderInfo(previewBoard.name).label} (이동 대상)` : undefined}
          subtitleColor="#fa8c16"
          count={displayMains.length}
          loading={loadingMains}
          headerAction={addButton("main", selectedBoardId == null || !!dragPreview)}
        >
          {selectedBoardId == null ? (
            hint("게시판을 선택하세요")
          ) : (
            <SortableContext
              items={displayMains.map((c) => dndId("main", c.id))}
              strategy={verticalListSortingStrategy}
            >
              {displayMains.map((c) => (
                <SortableRow
                  key={c.id}
                  dndIdStr={dndId("main", c.id)}
                  label={c.name}
                  selected={c.id === selectedMainId}
                  preview={subPreview?.mainId === c.id}
                  onClick={() => !dragPreview && selectMain(c.id)}
                  right={dragPreview ? undefined : catRowActions("main", c)}
                />
              ))}
              {displayMains.length === 0 && !loadingMains && <Empty description="카테고리 없음" />}
            </SortableContext>
          )}
        </ColumnShell>

        {/* 서브 카테고리 */}
        <ColumnShell
          title="서브 카테고리"
          subtitle={subPreview ? `→ ${subPreview.mainName} (이동 대상)` : undefined}
          subtitleColor="#fa8c16"
          count={displaySubs.length}
          loading={loadingSubs}
          headerAction={addButton("sub", selectedMainId == null || !!subPreview)}
        >
          {subPreview ? (
            displaySubs.length === 0 ? (
              hint(subsFetchingRef.current.has(subPreview.mainId) ? "불러오는 중..." : "서브 카테고리 없음")
            ) : (
              <SortableContext
                items={displaySubs.map((c) => dndId("sub", c.id))}
                strategy={verticalListSortingStrategy}
              >
                {displaySubs.map((c) => (
                  <SortableRow key={c.id} dndIdStr={dndId("sub", c.id)} label={c.name} />
                ))}
              </SortableContext>
            )
          ) : selectedMainId == null ? (
            hint("메인 카테고리를 선택하세요")
          ) : subCats.length === 0 ? (
            hint(loadingSubs ? "" : "서브 카테고리 없음 · 게시글은 오른쪽 컬럼")
          ) : (
            <SortableContext
              items={subCats.map((c) => dndId("sub", c.id))}
              strategy={verticalListSortingStrategy}
            >
              {subCats.map((c) => (
                <SortableRow
                  key={c.id}
                  dndIdStr={dndId("sub", c.id)}
                  label={c.name}
                  selected={c.id === selectedSubId}
                  onClick={() => selectSub(c.id)}
                  right={catRowActions("sub", c)}
                />
              ))}
            </SortableContext>
          )}
        </ColumnShell>

        {/* 게시글 */}
        <ColumnShell
          title="게시글"
          subtitle={leafLabel}
          count={posts.length}
          loading={loadingPosts}
          headerAction={
            <Button
              size="small"
              type="text"
              icon={<PlusOutlined />}
              disabled={leafCategoryId == null || loadingPosts}
              onClick={openAddPost}
            >
              추가
            </Button>
          }
        >
          {leafCategoryId == null ? (
            hint("카테고리를 선택하세요")
          ) : posts.length === 0 ? (
            loadingPosts ? null : <Empty description="게시글 없음" />
          ) : (
            <SortableContext
              items={posts.map((p) => dndId("post", p.id))}
              strategy={verticalListSortingStrategy}
            >
              {posts.map((p) => (
                <SortableRow
                  key={p.id}
                  dndIdStr={dndId("post", p.id)}
                  label={p.title}
                  onClick={() => goToPost(p)}
                  right={postRowActions(p)}
                />
              ))}
            </SortableContext>
          )}
        </ColumnShell>
      </div>

      <DragOverlay>{activeLabel ? <OverlayRow label={activeLabel} /> : null}</DragOverlay>
    </DndContext>

    <Modal
      open={!!catModal}
      title={
        catModal?.type === "add"
          ? catModal.column === "main"
            ? "메인 카테고리 추가"
            : "서브 카테고리 추가"
          : "카테고리 이름 수정"
      }
      okText={catModal?.type === "add" ? "추가" : "수정"}
      cancelText="취소"
      confirmLoading={catSubmitting}
      onOk={submitCatModal}
      onCancel={() => setCatModal(null)}
      destroyOnClose
      centered
    >
      <Input
        value={catName}
        onChange={(e) => setCatName(e.target.value)}
        onPressEnter={submitCatModal}
        placeholder="카테고리 이름"
        autoFocus
        maxLength={100}
      />
    </Modal>

    <Modal
      open={!!postModal}
      title={
        postModal?.type === "add"
          ? `게시글 추가${leafLabel ? ` · ${leafLabel}` : ""}`
          : "게시글 이름 수정"
      }
      okText={postModal?.type === "add" ? "추가" : "수정"}
      cancelText="취소"
      confirmLoading={postSubmitting}
      onOk={submitPostModal}
      onCancel={() => setPostModal(null)}
      destroyOnClose
      centered
    >
      <Input
        value={postTitle}
        onChange={(e) => setPostTitle(e.target.value)}
        onPressEnter={submitPostModal}
        placeholder="게시글 이름"
        autoFocus
        maxLength={255}
      />
    </Modal>
    </>
  );
};

/* 컬럼 껍데기 */
const ColumnShell = ({
  title,
  subtitle,
  subtitleColor = "#1677ff",
  count,
  loading,
  headerAction,
  children,
}: {
  title: string;
  subtitle?: string;
  subtitleColor?: string;
  count?: number;
  loading?: boolean;
  headerAction?: React.ReactNode;
  children: React.ReactNode;
}) => (
  <div
    style={{
      flex: 1,
      minWidth: 220,
      background: "#fafafa",
      border: "1px solid #f0f0f0",
      borderRadius: 8,
      display: "flex",
      flexDirection: "column",
      maxHeight: "70vh",
    }}
  >
    <div
      style={{
        padding: "10px 12px",
        borderBottom: "1px solid #f0f0f0",
        fontWeight: 600,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <span style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
        <span>{title}</span>
        {subtitle && (
          <span
            style={{
              fontSize: 12,
              fontWeight: 400,
              color: subtitleColor,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {subtitle}
          </span>
        )}
      </span>
      <span style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
        {loading && <Spin size="small" />}
        {typeof count === "number" && <span style={{ color: "#999", fontWeight: 400 }}>{count}</span>}
        {headerAction}
      </span>
    </div>
    <div style={{ padding: 10, overflowY: "auto", flex: 1 }}>{children}</div>
  </div>
);

export default CategoryDndEditor;
